import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Typography, DatePicker, Table, Tag, message, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { formatTS } from '../utils/time';

const { RangePicker } = DatePicker;

const STATUS_COLOR = {
  open:      'blue',
  complete:  'green',
  inactive:  'gold',
  cancelled: 'default',
};

let _cache = null;

export default function MyComplianceIssuesPage() {
  const navigate = useNavigate();

  const defaultRange = [dayjs().subtract(3, 'month').startOf('month'), dayjs()];

  const restoredRef = useRef(!!_cache);
  const [rows,      setRows]      = useState(() => _cache?.rows      ?? []);
  const [loading,   setLoading]   = useState(false);
  const [dateRange, setDateRange] = useState(() => _cache?.dateRange ?? defaultRange);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { mine: '1', type: 'incident' };
      if (dateRange?.[0]) params.date_from = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.date_to   = dateRange[1].format('YYYY-MM-DD');
      const res = await api.get('/api/compliance', { params });
      setRows(res.data);
      _cache = { rows: res.data, dateRange };
    } catch {
      message.error('Failed to load compliance issues.');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    if (restoredRef.current) { restoredRef.current = false; return; }
    load();
  }, [load]);

  const columns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 70 },
    {
      title: 'Status', dataIndex: 'Status', key: 'Status', width: 110,
      render: (v) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v ? v.charAt(0).toUpperCase() + v.slice(1) : '—'}</Tag>,
    },
    { title: 'Date of Incident', dataIndex: 'DateOfIncident', key: 'DateOfIncident', width: 140 },
    { title: 'Team',   dataIndex: 'TeamName',  key: 'TeamName',  ellipsis: true },
    {
      title: 'Issues', dataIndex: 'Issues', key: 'Issues',
      render: (v) => (v || []).join(', ') || '—',
    },
    // Red and flagged only while it still matters. Once acknowledged the
    // deadline is history, so it reads plainly next to the date it was met.
    { title: 'Acknowledge Deadline', dataIndex: 'AcknowledgeDueDate', key: 'AcknowledgeDueDate', width: 160,
      render: (v, r) => {
        if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
        if (r.AcknowledgedAt) return v;
        const over = dayjs().isAfter(dayjs(v), 'day');
        return (
          <span style={{ color: over ? '#ff4d4f' : '#fa8c16', fontWeight: over ? 600 : undefined }}>
            {v}{over ? ' (overdue)' : ''}
          </span>
        );
      } },
    { title: 'Acknowledged', dataIndex: 'AcknowledgedAt', key: 'AcknowledgedAt', width: 175,
      render: (v) => v ? formatTS(v) : <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Submitted', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 175 },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>My Compliance Incidents</Typography.Title>
        <Space wrap>
          <RangePicker
            placeholder={['Date From', 'Date To']}
            value={dateRange}
            onChange={(v) => setDateRange(v ?? defaultRange)}
            format="YYYY-MM-DD"
          />
        </Space>
      </div>

      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} issue${t === 1 ? '' : 's'}` }}
        onRow={(r) => ({ onClick: () => navigate(`/compliance/my-issues/${r.ID}`), style: { cursor: 'pointer' } })}
        locale={{ emptyText: 'No compliance issues found.' }}
      />
    </>
  );
}
